/**
 * Page / DOM scanner.
 *
 * Detects which Smootify-style attributes are present in the current document
 * and classifies the page type so the top-level boot code can decide what to
 * hydrate.
 *
 * The scanner is read-only; it does not mutate the DOM.
 */

import { config } from "../config.js";

export type PageKind =
  | "product"
  | "cart"
  | "collection"
  | "search"
  | "account"
  | "home"
  | "unknown";

export interface ScanHints {
  /** There is at least one `<smootify-product>` element. */
  hasSmootifyProduct: boolean;
  /** There is at least one `[data-product-handle]` element. */
  hasDataProductHandle: boolean;
  /** Any node with `cart="count"` is present. */
  hasCartCount: boolean;
  /** Any node with a `cart-item` attribute is present. */
  hasCartItem: boolean;
  /** Any collection-template element — `[limit]` + `product="..."` child. */
  hasCollectionList: boolean;
  /** `[search="title"]` / `[search="image"]` present. */
  hasSearchResults: boolean;
  /** URL path matched `/{productsBase}/{handle}`. */
  urlProductHandle: string | null;
}

export interface ScanResult {
  kind: PageKind;
  hints: ScanHints;
}

// ---------------------------------------------------------------------------
//  URL helpers
// ---------------------------------------------------------------------------

/**
 * Parse the product handle out of the current URL. Uses
 * `config.productsBase` as the prefix segment.
 *
 * Accepts paths of the form `/{productsBase}/{handle}` or
 * `/{productsBase}/{handle}/…`. Returns `null` if the URL does not match.
 */
export function resolveProductHandleFromURL(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "/",
): string | null {
  const base = config.productsBase.replace(/^\/+|\/+$/g, "");
  if (!base) return null;

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length < 2) return null;

  // Find the base segment; Webflow may nest it under a locale segment.
  const baseIndex = segments.indexOf(base);
  if (baseIndex < 0) return null;

  const handle = segments[baseIndex + 1];
  if (!handle) return null;

  try {
    return decodeURIComponent(handle);
  } catch {
    return handle;
  }
}

/**
 * Same as above but for the collections base.
 */
export function resolveCollectionHandleFromURL(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "/",
): string | null {
  const base = config.collectionsBase.replace(/^\/+|\/+$/g, "");
  if (!base) return null;

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length < 2) return null;

  const baseIndex = segments.indexOf(base);
  if (baseIndex < 0) return null;

  const handle = segments[baseIndex + 1];
  if (!handle) return null;

  try {
    return decodeURIComponent(handle);
  } catch {
    return handle;
  }
}

// ---------------------------------------------------------------------------
//  Scan
// ---------------------------------------------------------------------------

function collectHints(root: ParentNode): ScanHints {
  const hasSmootifyProduct = root.querySelector("smootify-product") !== null;
  const hasDataProductHandle = root.querySelector("[data-product-handle]") !== null;
  const hasCartCount = root.querySelector('[cart="count"]') !== null;
  const hasCartItem = root.querySelector("[cart-item]") !== null;

  // Collection templates typically have a `[limit]` wrapper whose children
  // carry `product="..."` attributes. We keep the heuristic loose.
  const hasCollectionList =
    root.querySelector("[limit] [product], [limit][product]") !== null;

  const hasSearchResults =
    root.querySelector('[search="title"], [search="image"], [search="url"]') !== null;

  const urlProductHandle = resolveProductHandleFromURL();

  return {
    hasSmootifyProduct,
    hasDataProductHandle,
    hasCartCount,
    hasCartItem,
    hasCollectionList,
    hasSearchResults,
    urlProductHandle,
  };
}

function classify(hints: ScanHints): PageKind {
  // Product: either explicit <smootify-product>, [data-product-handle], or URL match.
  if (hints.hasSmootifyProduct || hints.hasDataProductHandle) return "product";
  if (hints.urlProductHandle) return "product";

  // A cart page has cart-item repetition AND the cart count indicator, but we
  // classify dedicated cart pages on the presence of `cart-item` templates.
  if (hints.hasCartItem) return "cart";

  if (hints.hasSearchResults) return "search";
  if (hints.hasCollectionList) return "collection";

  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    const accountBase = config.accountBase.replace(/^\/+|\/+$/g, "");
    if (accountBase && path.includes(`/${accountBase}`)) return "account";
    if (path === "/" || path === "") return "home";
  }

  return "unknown";
}

/**
 * Scan the current document (or a provided root) and return a page-kind
 * classification with hints.
 */
export function scanPage(root: ParentNode = document): ScanResult {
  const hints = collectHints(root);
  const kind = classify(hints);
  return { kind, hints };
}
